/**
 * aeterna/conductor.ts — The Voice
 *
 * The cortex is the mind. The conductor is the voice.
 *
 * This module bridges the neural pipeline (cortex.ts) to the game world.
 * It owns the cortex, routes player messages through it, decides whether
 * the response comes from EntityZero or the offline system, composes the
 * response with structure (prelude/core/coda), manages typewriter delivery,
 * and handles the Aeterna's autonomous desire to speak.
 *
 * The conductor is the single integration point. The game loop creates one
 * conductor, and everything else follows from there.
 *
 * Design principle: the Aeterna is always present. When the API is available,
 * it speaks through the model. When it isn't, it speaks through the offline
 * responses. Both are the Aeterna. Neither is a fallback for the other.
 * They are two registers of the same mind.
 */

import { AeternaCortex, type CortexState, type MessageMeta, type SessionEndReport } from "./neural/cortex.js";
import { buildRoomContext, buildRoomFeatures, deriveRecentActivities } from "./bridge.js";
import { buildSystemPrompt, buildTurnContext, buildAPIMessages, buildOfflineContext, buildEntityZeroRequest, type ConversationMessage } from "./context.js";
import { getThinkingIndicator, getProactiveMessage } from "./offline_responses.js";
import { generateResponse, type GeneratorContext } from "./generator.js";
import { composeResponse, toDeliveryChunks, checkSacredResponse, type ResponseScene, type DeliveryChunk } from "./composition.js";
import { streamCall, isAvailable, type EntityZeroConfig, type StreamCallbacks } from "./api.js";
import type { Room, TimeState } from "../engine/state.js";
import type { AeternaPresenceState } from "./presence.js";
import type { RoomContext } from "./neural/hdc.js";
import type { RoomFeatures } from "./neural/crossmodal.js";

// ─── Conversation Message ──────────────────────────────────────────────────

export interface AeternaMessage {
  id: string;
  role: "player" | "aeterna";
  text: string;
  timestamp: number;
  /** For aeterna messages: structured delivery chunks for typewriter */
  chunks?: DeliveryChunk[];
  /** Whether this was a proactive (unprompted) message */
  proactive?: boolean;
  /** Ambient thought (not a conversation turn, just the Aeterna noticing) */
  ambient?: boolean;
}

// ─── Typing State ──────────────────────────────────────────────────────────

export interface TypingState {
  /** Is the typewriter currently active? */
  active: boolean;
  /** Full text being typed out */
  fullText: string;
  /** Characters revealed so far */
  revealed: number;
  /** Delivery chunks (prelude, core, coda) */
  chunks: DeliveryChunk[];
  /** Index of current chunk being typed */
  currentChunk: number;
  /** Characters revealed within current chunk */
  chunkRevealed: number;
  /** Timestamp when the current inter-chunk pause ends (0 = not pausing) */
  pauseUntil: number;
}

// ─── Conductor State (exposed to UI) ───────────────────────────────────────

export interface AeternaConversation {
  /** Full conversation history */
  history: AeternaMessage[];
  /** Is the Aeterna currently "thinking" before responding? */
  thinking: boolean;
  /** Thinking indicator text */
  thinkingText: string;
  /** Typewriter delivery state */
  typing: TypingState;
  /** Whether the conversation panel is scrolled to bottom */
  autoScroll: boolean;
  /** Scroll offset in pixels (from bottom) */
  scrollOffset: number;
  /** Total messages exchanged (monotonic counter) */
  totalExchanges: number;
}

// ─── Config ─────────────────────────────────────────────────────────────────

/** Typewriter speed: characters per second */
const TYPEWRITER_CPS = 28;
/** Minimum thinking delay before showing response (ms) — feels deliberate */
const MIN_THINK_DELAY = 600;
/** Maximum thinking delay for offline responses (ms) */
const MAX_OFFLINE_THINK = 1800;
/** How often to push ambient thoughts into conversation (seconds of silence) */
const AMBIENT_THOUGHT_INTERVAL = 120;
/** Maximum conversation history kept in memory */
const MAX_HISTORY = 200;

// ─── The Conductor ──────────────────────────────────────────────────────────

export class AeternaConductor {
  /** The neural pipeline */
  private cortex: AeternaCortex;
  /** Conversation state (exposed to UI via getConversation()) */
  private conversation: AeternaConversation;
  /** Flat history for API context building */
  private apiHistory: Array<{ role: "player" | "aeterna"; text: string }> = [];
  /** Sacred response triggers (one-shot) */
  private sacredTriggered = new Set<string>();
  /** EntityZero API config (null = offline only) */
  private apiConfig: Partial<EntityZeroConfig> | null = null;
  /** Total sessions (for sacred responses) */
  private totalSessions = 1;
  /** Absence hours (for sacred responses) */
  private absenceHours = 0;
  /** Accumulated silence for ambient thought injection */
  private silenceSinceLastMessage = 0;
  /** Last ambient thought time (prevents spam) */
  private lastAmbientTime = 0;
  /** Whether the cortex warmup is done */
  private warmupDone = false;
  /** Thinking start timestamp (for min delay) */
  private thinkStartTime = 0;
  /** Pending response to deliver after think delay */
  private pendingResponse: ResponseScene | null = null;
  /** Last cortex state (for context building) */
  private lastCortexState: CortexState | null = null;
  /** Room + time cache for context building */
  private cachedRoom: Room | null = null;
  private cachedTime: TimeState | null = null;
  /** Presence state reference (for context building) */
  private presence: AeternaPresenceState | null = null;
  /** Live curiosity pool built from cortex curiosity drive */
  private curiosityPool: import("./curiosity.js").OpenCuriosity[] = [];

  constructor(seed?: number) {
    this.cortex = new AeternaCortex(seed ?? Date.now());

    this.conversation = {
      history: [],
      thinking: false,
      thinkingText: "",
      typing: {
        active: false,
        fullText: "",
        revealed: 0,
        chunks: [],
        currentChunk: 0,
        chunkRevealed: 0,
        pauseUntil: 0,
      },
      autoScroll: true,
      scrollOffset: 0,
      totalExchanges: 0,
    };
  }

  // ─── Lifecycle ──────────────────────────────────────────────────────────

  /** Begin a conversation session */
  beginSession(): void {
    this.cortex.beginSession();
    this.silenceSinceLastMessage = 0;
    this.lastAmbientTime = 0;
  }

  /** End a conversation session — runs consolidation, dreaming, evolution */
  endSession(): SessionEndReport {
    return this.cortex.endSession();
  }

  /** Configure the EntityZero API endpoint */
  configureAPI(config: Partial<EntityZeroConfig>): void {
    this.apiConfig = config;
  }

  /** Set session metadata for sacred response windows */
  setSessionContext(totalSessions: number, absenceHours: number): void {
    this.totalSessions = totalSessions;
    this.absenceHours = absenceHours;
  }

  // ─── Frame Tick ─────────────────────────────────────────────────────────

  /**
   * Called every frame by the game loop.
   * Advances the typewriter, checks for proactive initiation, ticks cortex.
   */
  tick(dt: number, gameHour: number, presenceState?: AeternaPresenceState): void {
    // Tick the cortex (drives, polyvagal decay, proactive check, warmup)
    this.cortex.tick(dt);
    if (presenceState) this.presence = presenceState;

    // Track silence
    this.silenceSinceLastMessage += dt;

    // ── Thinking delay: deliver pending response after min delay ──────
    if (this.pendingResponse && !this.conversation.typing.active) {
      const elapsed = Date.now() - this.thinkStartTime;
      if (elapsed >= MIN_THINK_DELAY) {
        this.deliverResponse(this.pendingResponse);
        this.pendingResponse = null;
        this.conversation.thinking = false;
      }
    }

    // ── Typewriter: reveal characters ────────────────────────────────
    this.advanceTypewriter(dt);

    // ── Ambient thought injection ────────────────────────────────────
    // If silence has been long enough and no typing is active,
    // inject an ambient thought into the conversation.
    if (
      this.silenceSinceLastMessage > AMBIENT_THOUGHT_INTERVAL &&
      Date.now() - this.lastAmbientTime > AMBIENT_THOUGHT_INTERVAL * 1000 &&
      !this.conversation.thinking &&
      !this.conversation.typing.active &&
      this.conversation.history.length > 0 // only after first exchange
    ) {
      this.injectAmbientThought(presenceState);
    }
  }

  // ─── Player Message ─────────────────────────────────────────────────────

  /**
   * Process a player message. This is the main entry point.
   *
   * Flow:
   *   1. Add player message to history
   *   2. Run cortex.processMessage() — full neural pipeline
   *   3. Check for sacred response (one-shot milestone moments)
   *   4. Decide: API call or offline response
   *   5. Compose response with structure (prelude/core/coda)
   *   6. Enter "thinking" state with deliberate delay
   *   7. Deliver via typewriter
   */
  async processPlayerMessage(
    text: string,
    meta?: MessageMeta,
  ): Promise<void> {
    // Cancel any pending typing or thinking
    this.cancelCurrentDelivery();

    // Add player message
    const playerMsg: AeternaMessage = {
      id: `p_${Date.now()}`,
      role: "player",
      text,
      timestamp: Date.now(),
    };
    this.conversation.history.push(playerMsg);
    this.apiHistory.push({ role: "player", text });
    this.trimHistory();

    this.silenceSinceLastMessage = 0;
    this.conversation.totalExchanges++;

    // ── Run the neural pipeline ──────────────────────────────────────
    const roomContext = this.cachedRoom && this.cachedTime
      ? buildRoomContext(this.cachedRoom, this.cachedTime) : undefined;
    const roomFeatures = this.cachedRoom && this.cachedTime
      ? buildRoomFeatures(this.cachedRoom, this.cachedTime) : undefined;

    const cortexState = this.cortex.processMessage(text, roomContext, roomFeatures, meta);
    this.lastCortexState = cortexState;

    // ── Enter thinking state ─────────────────────────────────────────
    this.conversation.thinking = true;
    this.conversation.thinkingText = getThinkingIndicator(this.cachedTime?.gameHour ?? 12);
    this.thinkStartTime = Date.now();

    // ── Check sacred response (milestone moments) ────────────────────
    // Sacred responses bypass normal composition — they are complete as they stand.
    const fp = this.buildMinimalFingerprint(text, cortexState);
    const depthScore = cortexState.immediateAffect.depth > 0.6 ? 0.85 : 0.3;
    const sacred = checkSacredResponse(
      fp,
      this.totalSessions,
      this.cachedTime?.gameHour ?? 12,
      this.absenceHours,
      depthScore,
      this.sacredTriggered,
    );

    if (sacred) {
      this.scheduleResponse({
        prelude: null, core: sacred, coda: null, full: sacred,
        timing: { pauseAfterPrelude: 0, pauseAfterCore: 0 },
        surfacedCuriosityId: null,
      });
      return;
    }

    // ── API or Offline? ──────────────────────────────────────────────
    if (cortexState.shouldCallAPI && this.apiConfig) {
      const available = await isAvailable(this.apiConfig);
      if (available) {
        await this.handleAPIResponse(text, cortexState);
        return;
      }
    }

    // ── Offline response path ────────────────────────────────────────
    this.handleOfflineResponse(text, cortexState);
  }

  // ─── API Response Path ──────────────────────────────────────────────────

  private async handleAPIResponse(text: string, cortexState: CortexState): Promise<void> {
    const personality = cortexState.personality;

    // Build the full API request
    // The context envelope from the cortex is injected as part of the system prompt,
    // giving the model the richest possible frame for its response.
    const systemPrompt = this.buildSystemPromptWithEnvelope(cortexState);
    const turnContext = this.buildTurnContextFromState(text, cortexState);
    const messages = buildAPIMessages(systemPrompt, turnContext, this.apiHistory, text);
    const presencePhase = this.presence?.phase ?? "listening";
    const request = buildEntityZeroRequest(messages, presencePhase, personality.wit);

    // Stream the response token by token
    const responseText: string[] = [];

    // Cancel thinking indicator — we're streaming now
    this.conversation.thinking = false;

    // Create a streaming Aeterna message
    const streamMsg: AeternaMessage = {
      id: `a_${Date.now()}`,
      role: "aeterna",
      text: "",
      timestamp: Date.now(),
    };
    this.conversation.history.push(streamMsg);

    const callbacks: StreamCallbacks = {
      onToken: (token: string) => {
        responseText.push(token);
        streamMsg.text = responseText.join("");
        // Direct streaming — no typewriter needed, tokens arrive naturally
      },
      onComplete: (fullText: string) => {
        streamMsg.text = fullText;
        this.apiHistory.push({ role: "aeterna", text: fullText });
        this.conversation.autoScroll = true;
        // Snapshot pre-response affect for implicit feedback on next player message
        this.cortex.recordAPIResponse();
        // Self-model: process own words through affect/memory/attention
        this.cortex.processOwnResponse(fullText);
      },
      onError: (_error: Error) => {
        // Streaming failed mid-way — fall back to offline
        // Remove the partial streaming message
        const idx = this.conversation.history.indexOf(streamMsg);
        if (idx >= 0) this.conversation.history.splice(idx, 1);
        // Use offline response instead
        this.handleOfflineResponse(text, cortexState);
      },
    };

    await streamCall(request, callbacks, this.apiConfig ?? undefined);
  }

  // ─── Offline Response Path ──────────────────────────────────────────────

  private handleOfflineResponse(text: string, cortexState: CortexState): void {
    // Build recent response list for anti-repetition (last 20 Aeterna messages)
    const recentResponses = this.conversation.history
      .filter(m => m.role === "aeterna" && !m.ambient && !m.proactive)
      .slice(-20)
      .map(m => m.text);

    // Build room mood from cached room state
    const rawMood = this.cachedRoom?.mood;
    const roomMood = rawMood
      ? { warmth: rawMood.warmth, energy: rawMood.energy, mystery: rawMood.mystery, nature: rawMood.nature ?? 0.2 }
      : undefined;

    // Build MeMo recall summaries from cortex state
    const memoRecalls = cortexState.memoRecalls.map(r => ({
      summary: r.entry.summary,
      layer: r.layer,
    }));

    // Compose with the generator — context-aware, compositional, grounded
    const genCtx: GeneratorContext = {
      playerText: text,
      cortex: cortexState,
      recentResponses,
      personality: cortexState.personality,
      roomMood,
      gameHour: this.cachedTime?.gameHour,
      season: this.cachedTime?.season,
      memoRecalls: memoRecalls.length > 0 ? memoRecalls : undefined,
    };
    const generatedText = generateResponse(genCtx);

    const fp = this.buildMinimalFingerprint(text, cortexState);

    // Update curiosity pool from cortex curiosity drive
    this.updateCuriosityPool(cortexState);

    // Compose with structure (prelude, core, coda)
    const scene = composeResponse(
      generatedText,
      fp,
      this.curiosityPool,
      null, // rhythm anomaly — surfaces via presence/context, not composition
      { totalSessions: this.totalSessions, peakHour: 20, quietHour: 4, avgSessionLength: 10, preferredDay: "any" } as never,
      this.presence ?? { phase: "listening" } as never,
      this.cachedTime?.gameHour ?? 12,
    );

    // Schedule with thinking delay (deliberate pause before responding)
    this.scheduleResponse(scene);
  }

  /**
   * Populate the curiosity pool from the cortex's curiosity drive.
   * Converts neural-level curiosity signals into composition-level OpenCuriosity objects.
   */
  private updateCuriosityPool(cortexState: CortexState): void {
    const curiosity = cortexState.curiosity;
    if (!curiosity.wantsToAsk || !curiosity.questionDirection) return;

    // Don't duplicate — check if this direction already exists
    const direction = curiosity.questionDirection;
    const existing = this.curiosityPool.find(c => c.question === direction);
    if (existing) {
      // Refresh salience
      existing.salience = Math.min(1, existing.salience + 0.1);
      return;
    }

    // Create a new open curiosity from the cortex signal
    this.curiosityPool.push({
      id: `cur_${Date.now()}`,
      question: direction,
      preludeHint: "Something I've been wondering about.",
      source: "player_mention",
      salience: curiosity.overallCuriosity,
      resolvesOn: ["self"],
      createdAt: this.cachedTime?.gameHour ?? 12,
      weight: curiosity.overallCuriosity,
      surfacedCount: 0,
      resolved: false,
    });

    // Cap pool at 10
    if (this.curiosityPool.length > 10) {
      // Drop lowest salience
      this.curiosityPool.sort((a, b) => b.salience - a.salience);
      this.curiosityPool.length = 10;
    }
  }

  // ─── Proactive Initiation ───────────────────────────────────────────────

  /**
   * Wire up proactive initiation. Called once during conductor setup.
   * When the cortex decides the Aeterna wants to speak unprompted,
   * this generates and delivers the proactive message.
   */
  wireProactiveInitiation(): void {
    this.cortex.onProactiveInitiation((initiation) => {
      // Don't interrupt if already typing or thinking
      if (this.conversation.thinking || this.conversation.typing.active) return;

      // Generate a proactive message based on the reason
      const text = this.generateProactiveMessage(initiation);
      if (!text) return;

      const scene: ResponseScene = {
        prelude: null,
        core: text,
        coda: null,
        full: text,
        timing: { pauseAfterPrelude: 0, pauseAfterCore: 0 },
        surfacedCuriosityId: null,
      };

      // Add as proactive message
      const msg: AeternaMessage = {
        id: `a_pro_${Date.now()}`,
        role: "aeterna",
        text,
        timestamp: Date.now(),
        proactive: true,
        chunks: toDeliveryChunks(scene),
      };
      this.conversation.history.push(msg);
      this.apiHistory.push({ role: "aeterna", text });
      this.trimHistory();

      // Deliver via typewriter
      this.startTypewriter(toDeliveryChunks(scene), text);

      this.silenceSinceLastMessage = 0;
    });
  }

  private generateProactiveMessage(initiation: { reason: string; direction: string }): string | null {
    // Normalize cortex reason key to match PROACTIVE_OPENERS keys
    const reason = initiation.reason === "connection_seeking" ? "connection" : initiation.reason;
    const rawMood = this.cachedRoom?.mood;
    const roomMood = rawMood
      ? { warmth: rawMood.warmth, energy: rawMood.energy, mystery: rawMood.mystery }
      : undefined;
    const result = getProactiveMessage(reason, initiation.direction, roomMood, this.cachedTime?.gameHour);
    return result ?? null;
  }

  // ─── Ambient Thought Injection ──────────────────────────────────────────

  private injectAmbientThought(presenceState?: AeternaPresenceState): void {
    if (!presenceState) return;

    // Pull from the presence system's active ambient thought
    const activeThought = presenceState.activeThought;
    if (!activeThought || activeThought.text.length < 5) return;
    const thought = activeThought.text;

    const msg: AeternaMessage = {
      id: `a_amb_${Date.now()}`,
      role: "aeterna",
      text: thought,
      timestamp: Date.now(),
      ambient: true,
    };
    this.conversation.history.push(msg);
    this.trimHistory();

    this.lastAmbientTime = Date.now();
    this.silenceSinceLastMessage = 0;
  }

  // ─── Typewriter Engine ──────────────────────────────────────────────────

  private scheduleResponse(scene: ResponseScene): void {
    // Add a deliberate thinking delay proportional to response length
    const thinkDelay = Math.min(
      MAX_OFFLINE_THINK,
      MIN_THINK_DELAY + scene.full.length * 8,
    );

    // If thinking hasn't started yet (shouldn't happen, but safety)
    if (!this.conversation.thinking) {
      this.conversation.thinking = true;
      this.conversation.thinkingText = getThinkingIndicator(this.cachedTime?.gameHour ?? 12);
      this.thinkStartTime = Date.now();
    }

    // Calculate when the min delay will be satisfied
    const elapsed = Date.now() - this.thinkStartTime;
    if (elapsed >= thinkDelay) {
      // Already waited long enough (API took a while before falling back)
      this.deliverResponse(scene);
      this.conversation.thinking = false;
    } else {
      // Schedule for later — tick() will deliver it
      this.pendingResponse = scene;
    }
  }

  private deliverResponse(scene: ResponseScene): void {
    const chunks = toDeliveryChunks(scene);

    const msg: AeternaMessage = {
      id: `a_${Date.now()}`,
      role: "aeterna",
      text: scene.full,
      timestamp: Date.now(),
      chunks,
    };
    this.conversation.history.push(msg);
    this.apiHistory.push({ role: "aeterna", text: scene.full });
    this.trimHistory();

    // Start typewriter
    this.startTypewriter(chunks, scene.full);

    // Snapshot pre-response affect for implicit feedback on next player message
    this.cortex.recordAPIResponse();
    // Self-model: process own words through affect/memory/attention
    this.cortex.processOwnResponse(scene.full);
  }

  private startTypewriter(chunks: DeliveryChunk[], fullText: string): void {
    this.conversation.typing = {
      active: true,
      fullText,
      revealed: 0,
      chunks,
      currentChunk: 0,
      chunkRevealed: 0,
      pauseUntil: chunks[0]?.preDelayMs ? Date.now() + chunks[0].preDelayMs : 0,
    };
    this.conversation.autoScroll = true;
  }

  private advanceTypewriter(dt: number): void {
    const typing = this.conversation.typing;
    if (!typing.active) return;

    const now = Date.now();

    // Inter-chunk pause
    if (typing.pauseUntil > 0 && now < typing.pauseUntil) return;
    typing.pauseUntil = 0;

    // Reveal characters
    const charsToReveal = Math.ceil(TYPEWRITER_CPS * dt);
    const currentChunk = typing.chunks[typing.currentChunk];
    if (!currentChunk) {
      // All chunks done
      typing.active = false;
      typing.revealed = typing.fullText.length;
      return;
    }

    typing.chunkRevealed += charsToReveal;
    typing.revealed += charsToReveal;

    // Move to next chunk if current is done
    if (typing.chunkRevealed >= currentChunk.text.length) {
      typing.currentChunk++;

      // Check if there's a next chunk with a pause
      const next = typing.chunks[typing.currentChunk];
      if (next) {
        typing.chunkRevealed = 0;
        if (next.preDelayMs > 0) {
          typing.pauseUntil = now + next.preDelayMs;
        }
      } else {
        // Done
        typing.active = false;
        typing.revealed = typing.fullText.length;
      }
    }
  }

  private cancelCurrentDelivery(): void {
    // If typing, snap to complete
    if (this.conversation.typing.active) {
      this.conversation.typing.active = false;
      this.conversation.typing.revealed = this.conversation.typing.fullText.length;
    }
    // If thinking, cancel
    this.conversation.thinking = false;
    this.pendingResponse = null;
  }

  // ─── Room Context ───────────────────────────────────────────────────────

  /** Update the room context for the cortex. Call when room changes. */
  updateRoomContext(room: Room, time: TimeState): void {
    this.cachedRoom = room;
    this.cachedTime = time;

    const ctx = buildRoomContext(room, time, deriveRecentActivities(room));
    const feat = buildRoomFeatures(room, time);
    this.cortex.updateRoomContext(ctx, feat);
  }

  /** Update just the time (every frame or every few seconds) */
  updateTime(time: TimeState): void {
    this.cachedTime = time;
  }

  // ─── State Accessors ────────────────────────────────────────────────────

  /** Get the conversation state for UI rendering */
  getConversation(): Readonly<AeternaConversation> {
    return this.conversation;
  }

  /** Get the last cortex state (for debug/envelope inspection) */
  getLastCortexState(): CortexState | null {
    return this.lastCortexState;
  }

  /** Get the current typewriter revealed text for the latest message */
  getRevealedText(): string {
    const typing = this.conversation.typing;
    if (!typing.active) return "";

    // Build revealed text from chunks
    let text = "";
    for (let i = 0; i < typing.currentChunk; i++) {
      if (text.length > 0) text += "\n\n";
      text += typing.chunks[i].text;
    }
    const current = typing.chunks[typing.currentChunk];
    if (current) {
      if (text.length > 0) text += "\n\n";
      text += current.text.slice(0, typing.chunkRevealed);
    }
    return text;
  }

  /** Is the Aeterna currently doing anything? (thinking, typing, etc.) */
  isBusy(): boolean {
    return this.conversation.thinking || this.conversation.typing.active;
  }

  /** Update scroll offset from UI */
  setScrollOffset(offset: number): void {
    this.conversation.scrollOffset = offset;
    // Disable auto-scroll when user scrolls up
    this.conversation.autoScroll = offset <= 0;
  }

  // ─── Context Building Helpers ──────────────────────────────────────────

  private buildSystemPromptWithEnvelope(cortexState: CortexState): string {
    // The context envelope IS the richest summary of the Aeterna's current state.
    // We inject it directly into the system prompt so the model knows everything
    // the neural pipeline has computed.
    const personality = cortexState.personality;
    const rhythm = { totalSessions: this.totalSessions } as never;

    const basePrompt = buildSystemPrompt(
      { depth: this.conversation.totalExchanges / 100, currentTone: "neutral", absenceHours: this.absenceHours } as never,
      personality as never,
      this.curiosityPool,
      rhythm,
    );

    // Append the cortex's context envelope as additional grounding
    if (cortexState.contextEnvelope) {
      return basePrompt + "\n\n---\n\n[Neural state — what you're currently perceiving]\n" + cortexState.contextEnvelope;
    }
    return basePrompt;
  }

  private buildTurnContextFromState(text: string, cortexState: CortexState): string {
    const affect = cortexState.immediateAffect;
    const fp = this.buildMinimalFingerprint(text, cortexState);

    const gameHour = this.cachedTime?.gameHour ?? 12;
    const season   = this.cachedTime?.season   ?? "spring";
    const roomMood = this.cachedRoom?.mood ?? { warmth: 0.5, coziness: 0.5, mystery: 0.3, energy: 0.4 };

    const signals = {
      coRegStrategy: cortexState.coRegTarget.strategy,
      tension:       affect.tension,
      melancholy:    affect.melancholy,
      playfulness:   affect.playfulness,
      warmth:        affect.warmth,
      wordCount:     text.split(/\s+/).length,
      // First message of this session when totalExchanges is 0 or 1
      isFirstMessage: this.conversation.totalExchanges <= 1,
    };

    return buildTurnContext(
      fp,
      this.presence ?? { phase: "listening" } as never,
      gameHour,
      season,
      roomMood,
      this.curiosityPool,
      null, // anomaly — not tracked in conductor path
      undefined,
      {},
      signals,
    );
  }

  private buildMinimalFingerprint(text: string, cortexState: CortexState): import("./semantics.js").MessageFingerprint {
    // Build a fingerprint for composition.ts from cortex state
    const affect = cortexState.immediateAffect;
    const lower = text.toLowerCase();
    return {
      raw: text,
      valence: (affect.warmth + affect.playfulness) * 0.5 - (affect.tension + affect.melancholy) * 0.5,
      depth: affect.depth,
      novelty: cortexState.prediction.overallSurprise > 1 ? 0.7 : 0.3,
      isPersonal: lower.indexOf("i ") >= 0 || lower.indexOf("my ") >= 0,
      urgency: affect.urgency,
      wordCount: text.split(/\s+/).length,
      at: Date.now(),
      questionType: cortexState.intent.type === "question" ? "direct" : "none",
      signal: affect.tension > 0.5 ? "difficulty" : affect.depth > 0.5 ? "philosophical" : "warmth",
      topics: {
        room: lower.indexOf("room") >= 0 ? 0.8 : 0.1,
        self: lower.indexOf("i ") >= 0 ? 0.6 : 0.1,
        memory: lower.indexOf("remember") >= 0 ? 0.7 : 0.1,
        relationship: 0.2,
        philosophy: affect.depth > 0.5 ? 0.6 : 0.1,
        time: 0.1,
        emotion: affect.tension > 0.4 || affect.melancholy > 0.4 ? 0.6 : 0.2,
        world: 0.1,
        practical: lower.indexOf("how") >= 0 || lower.indexOf("help") >= 0 ? 0.5 : 0.1,
        aeterna: lower.indexOf("you") >= 0 ? 0.5 : 0.2,
      },
    } as import("./semantics.js").MessageFingerprint;
  }

  // ─── History Management ─────────────────────────────────────────────────

  private trimHistory(): void {
    if (this.conversation.history.length > MAX_HISTORY) {
      this.conversation.history.splice(0, this.conversation.history.length - MAX_HISTORY);
    }
    if (this.apiHistory.length > MAX_HISTORY) {
      this.apiHistory.splice(0, this.apiHistory.length - MAX_HISTORY);
    }
  }

  // ─── Serialization ──────────────────────────────────────────────────────

  serialize(): string {
    return JSON.stringify({
      cortex: this.cortex.serialize(),
      history: this.conversation.history.map(m => ({
        role: m.role,
        text: m.text,
        timestamp: m.timestamp,
        proactive: m.proactive,
        ambient: m.ambient,
      })),
      totalExchanges: this.conversation.totalExchanges,
      totalSessions: this.totalSessions,
      sacredTriggered: Array.from(this.sacredTriggered),
    });
  }

  static deserialize(json: string, seed?: number): AeternaConductor {
    const data = JSON.parse(json);
    const conductor = new AeternaConductor(seed);

    if (data.cortex) {
      const restoredCortex = AeternaCortex.deserialize(data.cortex, seed);
      (conductor as unknown as { cortex: AeternaCortex }).cortex = restoredCortex;
    }

    if (data.history) {
      conductor.conversation.history = data.history.map((m: AeternaMessage) => ({
        ...m,
        id: m.id || `${m.role[0]}_${m.timestamp}`,
      }));
      conductor.apiHistory = data.history
        .filter((m: AeternaMessage) => !m.ambient)
        .map((m: AeternaMessage) => ({ role: m.role, text: m.text }));
    }

    if (data.totalExchanges) conductor.conversation.totalExchanges = data.totalExchanges;
    if (data.totalSessions) conductor.totalSessions = data.totalSessions;
    if (data.sacredTriggered) conductor.sacredTriggered = new Set(data.sacredTriggered);

    return conductor;
  }
}
